import React, { useEffect, useMemo, useState, useCallback } from "react";
import GridTable from "cvmgmt-cube-grid-table";
import { Pagination } from "cprime-foundational-components";
import { ColumnFilter, SortingState } from "@tanstack/react-table";
import { GridTableDataType } from "cvmgmt-cube-grid-table/dist/types/src/GridTable/types";
import { TableHeader } from "cprime-datatable-components";
import * as styleScss from "./GridTable.module.scss";

type RowType = Record<string, any>;

interface SelectionTableProps {
  gridData: any;
  selectedRows: (string | number)[];
  onSelectionChange: (selectedIds: (string | number)[]) => void;
  isRowValid?: (row: RowType) => boolean;
  keyId: string;
  title?: string;
  style?: any;
  rowActions?: any;
  actions?: any;
  cellActions?: any;
  onRowClick?: any;
  loading?: boolean;
  setIsSubmitEnabled?: any;
  refresh?: number;
  filters: ColumnFilter[];
  sorting: SortingState;
  setFilters: any;
  setSorting: any;
  settings?: any;
  setSelectedData?: any;
  selectedData?: any;
  pagination?: any;
  setPagination?: any;
  tableHeader?: boolean;
  pinColumns?: any;
  hideSelectionBar?: boolean;
  hideSelectionCaret?: boolean;
}

const GridTableCommon: React.FC<SelectionTableProps> = ({
  gridData,
  selectedRows,
  onSelectionChange,
  isRowValid = () => true,
  keyId,
  title,
  style,
  rowActions,
  actions,
  cellActions,
  onRowClick,
  loading,
  setIsSubmitEnabled,
  refresh = 0,
  filters = [],
  setFilters,
  sorting = [],
  setSorting,
  settings,
  setSelectedData,
  selectedData,
  pagination,
  setPagination,
  tableHeader = true,
  pinColumns = [],
  hideSelectionBar = false,
  hideSelectionCaret = false,
}) => {
  const scssClass = styleScss as any;

  const [gridRefresh, setGridRefresh] = useState(refresh);
  const [resetAllSelection, setResetAllSelection] = useState(false);

  const handleResetSelection = useCallback(() => {
    setResetAllSelection((prev) => !prev);
  }, []);

  // Memoize the paginated rows to avoid recalculating on every render
  const paginatedRows = useMemo(() => {
    const sliced = (gridData?.rows ?? []).map((row: any) => {
      return {
        ...row,
        subRows: row.subRows ?? [],
        disabled: row?.disabled,
        isSelectable: row?.disabled ? false : null,
        enableRowSelection: row?.disabled ? false : null,
      };
    });

    if (selectedRows?.length > 0 && setIsSubmitEnabled) setIsSubmitEnabled();
    handleResetSelection();

    return sliced;
  }, [gridData?.rows, selectedRows]);

  useEffect(() => {
    setGridRefresh((prev) => prev + 1);
  }, [paginatedRows]);

  const handleSelectAll = useCallback(
    (_isAll: boolean, rowData: RowType[]) => {
      if (!rowData) return;

      const normalizedRows = rowData.map((row: any) => row?.original ?? row);
      const validRows = normalizedRows?.filter((row) => isRowValid(row));
      const validRowIds = validRows
        .map((row) => row?.[keyId])
        ?.filter((id) => id !== undefined && id !== null);

      const allValidRowsSelected =
        validRowIds.length > 0 &&
        validRowIds.every((id) => selectedRows.includes(id));

      const shouldSelectAllValidRows = !allValidRowsSelected;

      const updatedSelection = shouldSelectAllValidRows
        ? Array.from(new Set([...selectedRows, ...validRowIds]))
        : selectedRows?.filter((id) => !validRowIds.includes(id));

      onSelectionChange(updatedSelection);

      if (setSelectedData) {
        const existingSelectedData = selectedData ?? [];
        const selectDataId = existingSelectedData.map((row: any) => row[keyId]);
        const newSelectedData = shouldSelectAllValidRows
          ? [
              ...existingSelectedData,
              ...(validRows ?? []).filter(
                (row) => !selectDataId.includes(row[keyId]),
              ),
            ]
          : existingSelectedData?.filter(
              (row: any) => !validRowIds.includes(row[keyId]),
            );
        setSelectedData(newSelectedData);
      }
    },
    [selectedRows, setSelectedData, selectedData],
  );

  // Handle row selection
  const handleRowSelection = useCallback(
    (rowData: RowType, isRowSelected: boolean) => {
      if (!isRowValid(rowData)) {
        return;
      }
      const rowId = rowData[keyId];
      const updatedSelection = isRowSelected
        ? Array.from(new Set([...selectedRows, rowId]))
        : selectedRows?.filter((id) => id !== rowId);

      onSelectionChange(updatedSelection);

      if (setSelectedData) {
        const newSelectedData = isRowSelected
          ? Array.from(new Set([...selectedData, rowData]))
          : selectedData?.filter((row: any) => row[keyId] !== rowId);
        setSelectedData(newSelectedData);
      }
    },
    [selectedRows],
  );

  const vehicleConfig = {
    isBulkActionView: true,
    isExternalRowCount: true,
    reRenderFlag: resetAllSelection,
    selectedVehicleCount: selectedRows?.length,
    handleDelete: () => null,
    headerActions: ({ isSelectEntireGrid, rowData }: any) =>
      handleSelectAll(isSelectEntireGrid, rowData),
    columnActions: ({ isAllRowsSelected, rowData }: any) =>
      handleSelectAll(isAllRowsSelected, rowData),
    rowActions: ({ rowData, isRowSelected }: any) =>
      handleRowSelection(rowData, isRowSelected),
  };

  const sortedBy = useMemo(() => {
    if (!sorting?.[0]?.id) return "None";
    const sortBy = gridData?.columns?.find(
      (col: any) => col?.id === sorting[0]?.id,
    );
    return sortBy?.name || "None";
  }, [sorting, gridData?.columns]);

  const gridDataIn: GridTableDataType = useMemo(
    () => ({
      columns: (gridData?.columns ?? [])?.map((col: any) => ({
        ...col,
        size: col?.size ?? 200,
        minSize: col?.minSize ?? 200,
      })),
      rows: paginatedRows,
      totalCount: gridData?.totalCount,
    }),
    [gridData, paginatedRows],
  );

  const shouldUseGridHeader = Boolean(actions?.length);

  return (
    <div style={{ background: "white", width: "100%" }}>
      {tableHeader && !shouldUseGridHeader ? (
        <TableHeader
          title={title}
          details={[
            `${gridDataIn?.totalCount || 0} items`,
            `Sorted by ${sortedBy}`,
          ]}
          className={scssClass.tableHeader}
        />
      ) : null}
      <GridTable
        style={style}
        data={gridDataIn}
        isPagination={false}
        customPayload={false}
        refresh={gridRefresh}
        isCustomizeColumns={false}
        title={title || ""}
        isRowSelection={!hideSelectionBar}
        enabledRowSelection={(row: any) => {
          return row?.original?.disabled ? false : true;
        }}
        pinning={{ left: [], right: pinColumns ?? [] }}
        isFiltering={true}
        hideSelectionCaret={hideSelectionCaret}
        vehicleConfig={vehicleConfig}
        filters={filters}
        sorting={sorting}
        setSorting={setSorting}
        setFilters={setFilters}
        showHeader={shouldUseGridHeader}
        hideSelectionBar={hideSelectionBar}
        isSorting={true}
        showLoader={false}
        rowActions={rowActions}
        actions={actions}
        cellActions={cellActions}
        onRowClick={onRowClick}
        loading={loading}
        settings={settings}
      />
      <Pagination
        sx={{ width: "100%" }}
        {...{
          ...pagination,
          count: gridData?.totalCount ?? 0,
          rowsPerPageOptions: [...(tableHeader ? [] : [10]), 25, 50, 100, 250],
          onPageChange: (_e, number) =>
            setPagination({ ...pagination, page: number }),
          onRowsPerPageChange: (evt) =>
            setPagination({
              page: 0,
              rowsPerPage: Number(evt.target.value ?? 25),
            }),
        }}
      />
    </div>
  );
};

export default GridTableCommon;
